/* Bright&Shiny size guides. Public gallery API; no staff application or Shopify app dependency. */
(() => {
  'use strict';
  if (window.BSSizeChartsLoaded) return;
  window.BSSizeChartsLoaded = true;
  const API = 'https://gallery.brightandshinystore.com/api/v1/size-charts';
  const requests = new Map(), mounted = new WeakSet(), views = new Set(), selections = new Map();
  let unit = 'cm', dialog, dialogBody, opener, previousOverflow, activeRequest = 0;
  try { unit = localStorage.getItem('bs-size-unit') === 'in' ? 'in' : 'cm'; } catch {}
  const element = (tag, text, className) => {
    const e = document.createElement(tag);
    if (text != null) e.textContent = text;
    if (className) e.className = className;
    return e;
  };
  function validateChart(c) {
    if (!c || !Array.isArray(c.columns) || !c.columns.length || c.columns.length > 20 ||
        !c.columns.every(v => typeof v === 'string') || !Array.isArray(c.rows) || !c.rows.length || c.rows.length > 50 ||
        !['title','intro','note'].every(k => typeof c[k] === 'string')) throw new Error('Invalid chart');
    c.rows.forEach(r => {
      if (!r || typeof r.label !== 'string' || !['length','text'].includes(r.type) ||
          !['values','values_in'].every(k => Array.isArray(r[k]) && r[k].length === c.columns.length && r[k].every(v => typeof v === 'string'))) throw new Error('Invalid measurements');
    });
  }
  async function load(suffix, force = false) {
    const url = API + suffix, existing = requests.get(url);
    if (!force && existing && existing.until > Date.now()) return existing.promise;
    const record = {until:Date.now() + 60000};
    record.promise = (async () => {
      const controller = new AbortController(), timer = setTimeout(() => controller.abort(), 10000);
      try {
        const response = await fetch(url, {credentials:'omit',mode:'cors',signal:controller.signal,headers:{Accept:'application/json'}});
        if (response.status === 404 && suffix) return null;
        if (!response.ok) throw new Error('Size guide request failed');
        const data = await response.json();
        if (!data.ok || data.schema_version !== 1 || data.base_unit !== 'cm' || !data.design) throw new Error('Invalid response');
        if (suffix) {
          if (data.context !== 'product' || !data.product || typeof data.product.title !== 'string' || !Object.hasOwn(data,'chart')) throw new Error('Invalid product response');
          if (data.chart !== null) validateChart(data.chart);
        } else {
          if (data.context !== 'page' || !Array.isArray(data.charts)) throw new Error('Invalid page response');
          data.charts.forEach(validateChart);
        }
        return data;
      } finally { clearTimeout(timer); }
    })().catch(error => { if (requests.get(url) === record) requests.delete(url); throw error; });
    requests.set(url, record); return record.promise;
  }
  function applyDesign(host, design) {
    for (const key of ['header','stripe','text','highlight']) {
      if (/^#[a-f0-9]{6}$/i.test(design[key])) host.style.setProperty('--sc-' + key, design[key]);
    }
    if (/^#[a-f0-9]{6}$/i.test(design.text)) host.style.setProperty('--sc-cell-text', design.text.toLowerCase() === '#1f1c1c' ? 'rgba(0,0,0,.87)' : design.text);
    for (const [key, css, min, max] of [['font_size','font',12,20],['padding','pad',8,24]]) {
      const value = Number(design[key]); if (Number.isInteger(value) && value >= min && value <= max) host.style.setProperty('--sc-' + css, value + 'px');
    }
  }
  function setUnit(next) {
    unit = next;
    try { localStorage.setItem('bs-size-unit', unit); } catch {}
    for (const view of views) {
      if (!view.host.isConnected) { views.delete(view); continue; }
      view.buttons.forEach(([button, value]) => button.setAttribute('aria-pressed', String(unit === value)));
      view.cells.forEach(({button, row, column, title}) => {
        const value = (unit === 'cm' ? row.values : row.values_in)[column];
        button.textContent = value; button.setAttribute('aria-label', `${row.label} · ${title} · ${value}`);
      });
    }
  }
  function renderChart(c, design, product, key) {
    const host = element('section', null, 'bsc-chart' + (c.columns.length <= 4 ? ' bsc-narrow' : ''));
    applyDesign(host, design);
    const heading = element('h2', product ? 'Size guide' : c.title, product ? 'bs-size-heading' : '');
    if (product) heading.id = 'bs-size-dialog-title';
    host.append(heading);
    if (product) host.append(element('p', product.title, 'bsc-product'));
    if (c.intro) host.append(element('p', c.intro, 'bsc-intro'));
    const view = {host,buttons:[],cells:[]}; views.add(view);
    const controls = element('div', null, 'bsc-units'); controls.setAttribute('role','group'); controls.setAttribute('aria-label','Measurement units');
    for (const value of ['cm','in']) {
      if (value === 'in') { const hr = element('hr'); hr.setAttribute('aria-hidden','true'); controls.append(hr); }
      const b = element('button', value.toUpperCase()); b.type = 'button'; b.setAttribute('aria-label', value === 'cm' ? 'Centimeters' : 'Inches'); b.setAttribute('aria-pressed',String(value === unit));
      b.onclick = () => setUnit(value); controls.append(b); view.buttons.push([b,value]);
    }
    host.append(controls);
    const scroll = element('div', null, 'bsc-scroll'); scroll.tabIndex = 0; scroll.setAttribute('role','region'); scroll.setAttribute('aria-label', c.title + ' — scroll horizontally for more sizes');
    const table = element('table'), thead = element('thead'), tr = element('tr'); table.setAttribute('aria-label',c.title); tr.append(element('th',''));
    c.columns.forEach(name => { const th = element('th',name); th.scope = 'col'; tr.append(th); }); thead.append(tr); table.append(thead);
    const selected = selections.get(key) || new Set(); selections.set(key, selected);
    const tbody = element('tbody');
    c.rows.forEach((row, ri) => {
      const tr = element('tr'), th = element('th', row.label); th.scope = 'row'; tr.append(th);
      row.values.forEach((_, column) => {
        const td = element('td'), value = (unit === 'cm' ? row.values : row.values_in)[column], b = element('button', value), cell = ri + ':' + column;
        b.type = 'button'; b.setAttribute('aria-pressed',String(selected.has(cell))); b.setAttribute('aria-label',`${row.label} · ${c.columns[column]} · ${value}`);
        b.onclick = () => { if (selected.has(cell)) selected.delete(cell); else selected.add(cell); b.setAttribute('aria-pressed',String(selected.has(cell))); };
        view.cells.push({button:b,row,column,title:c.columns[column]}); td.append(b); tr.append(td);
      }); tbody.append(tr);
    });
    table.append(tbody); scroll.append(table); host.append(scroll);
    if (c.note) host.append(element('p', c.note, 'bsc-note'));
    return host;
  }
  function errorView(host, retry, fallback) {
    host.replaceChildren(); const box = element('div',null,'bs-size-message'); box.setAttribute('role','status');
    box.append(element('p','Size guides are temporarily unavailable. Please try again.'));
    const button = element('button','Try again','bs-size-retry'); button.type = 'button'; button.onclick = retry; box.append(button);
    if (fallback) { const link = element('a','View all size guides'); link.href = fallback; box.append(link); }
    host.append(box);
  }
  function ensureDialog() {
    if (dialog) return;
    dialog = element('dialog',null,'bs-size-dialog'); dialog.id = 'bs-size-dialog'; dialog.setAttribute('aria-label','Size guide');
    const close = element('button','×','bs-size-close'); close.type = 'button'; close.setAttribute('aria-label','Close size guide'); close.onclick = () => dialog.close();
    dialogBody = element('div'); dialog.append(close, dialogBody); document.body.append(dialog);
    dialog.addEventListener('click', e => { if (e.target !== dialog) return; const r = dialog.getBoundingClientRect(); if (e.clientX < r.left || e.clientX > r.right || e.clientY < r.top || e.clientY > r.bottom) dialog.close(); });
    dialog.addEventListener('close', () => {
      activeRequest++; document.body.style.overflow = previousOverflow;
      if (opener) { opener.setAttribute('aria-expanded','false'); if (opener.isConnected) opener.focus(); }
      for (const view of views) if (dialogBody.contains(view.host)) views.delete(view);
      dialogBody.replaceChildren();
    });
  }
  async function open(trigger, force = false) {
    ensureDialog();
    if (typeof dialog.showModal !== 'function') { window.location.assign(trigger.href); return; }
    opener = trigger; const request = ++activeRequest;
    dialog.removeAttribute('aria-labelledby'); dialogBody.replaceChildren(element('p','Loading size guide…','bs-size-message'));
    if (!dialog.open) { previousOverflow = document.body.style.overflow; dialog.showModal(); document.body.style.overflow = 'hidden'; }
    trigger.setAttribute('aria-expanded','true');
    try {
      const data = await load('/product-id/' + trigger.dataset.bsSizeProduct, force);
      if (request !== activeRequest || !dialog.open) return;
      if (!data?.chart) { trigger.hidden = true; dialogBody.replaceChildren(element('p','No size guide is available for this product.','bs-size-message')); return; }
      dialogBody.replaceChildren(renderChart(data.chart,data.design,{title:trigger.dataset.bsSizeTitle || data.product.title}, 'product:' + data.product.id + ':' + data.chart.id));
      dialog.setAttribute('aria-labelledby','bs-size-dialog-title');
    } catch { if (request === activeRequest && dialog.open) errorView(dialogBody, () => open(trigger,true), trigger.href); }
  }
  function mountProduct(trigger) {
    if (!/^[1-9][0-9]{0,19}$/.test(trigger.dataset.bsSizeProduct)) return;
    let ready = false;
    trigger.addEventListener('click', e => { if (!ready) return; e.preventDefault(); open(trigger); });
    load('/product-id/' + trigger.dataset.bsSizeProduct).then(data => {
      if (!trigger.isConnected || !data?.chart) return;
      ready = true; trigger.hidden = false; trigger.setAttribute('aria-haspopup','dialog'); trigger.setAttribute('aria-controls','bs-size-dialog'); trigger.setAttribute('aria-expanded','false');
    }).catch(() => { trigger.hidden = false; }); // Ordinary size-guide link remains usable on a network failure.
  }
  async function mountPage(host, force = false) {
    host.setAttribute('aria-busy','true'); host.replaceChildren(element('p','Loading size guides…','bs-size-message'));
    try {
      const data = await load('',force); if (!host.isConnected) return;
      host.replaceChildren(...data.charts.map(c => renderChart(c,data.design,null,'page:' + c.id)));
      if (!data.charts.length) host.append(element('p','No size guides are currently available.','bs-size-message'));
    } catch { if (host.isConnected) errorView(host, () => mountPage(host,true)); }
    finally { host.setAttribute('aria-busy','false'); }
  }
  function scan(root) {
    const selector = '[data-bs-size-product],[data-bs-size-page]';
    const nodes = [...(root.matches?.(selector) ? [root] : []), ...root.querySelectorAll(selector)];
    nodes.forEach(node => { if (mounted.has(node)) return; mounted.add(node); if (node.hasAttribute('data-bs-size-page')) mountPage(node); else mountProduct(node); });
  }
  scan(document);
  document.addEventListener('shopify:section:load', event => scan(event.target));
  new MutationObserver(records => records.forEach(record => record.addedNodes.forEach(node => { if (node.nodeType === 1) scan(node); }))).observe(document.body,{childList:true,subtree:true});
})();
